import type { Permission } from '@brechoflow/contracts';
import { Suspense } from 'react';
import { Navigate, NavLink, Outlet, useLocation } from 'react-router';
import { PageHeader } from '@/components/page-header';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/cn';
import { Forbidden } from '@/features/auth/guards';
import { useSession } from '@/features/auth/session';

export const SETTINGS_TABS: { to: string; label: string; permission: Permission }[] = [
  { to: 'loja', label: 'Loja', permission: 'settings.manage' },
  { to: 'unidades', label: 'Unidades', permission: 'settings.manage' },
  { to: 'horarios', label: 'Horários', permission: 'settings.manage' },
  { to: 'pagamentos', label: 'Pagamentos', permission: 'settings.manage' },
  { to: 'entrega', label: 'Entrega', permission: 'settings.manage' },
  { to: 'pedidos', label: 'Pedidos', permission: 'settings.manage' },
  { to: 'devolucoes', label: 'Trocas e devoluções', permission: 'settings.manage' },
  // FEATURE_SETTINGS_TABS: formas de pagamento, frete, etc. (módulos do brechó)
  { to: 'equipe', label: 'Equipe', permission: 'members.manage' },
  { to: 'convites', label: 'Convites', permission: 'members.manage' },
  { to: 'papeis', label: 'Papéis', permission: 'members.manage' },
  { to: 'whatsapp', label: 'WhatsApp', permission: 'settings.manage' },
  { to: 'assinatura', label: 'Assinatura', permission: 'settings.manage' },
  { to: 'auditoria', label: 'Auditoria', permission: 'audit.read' },
  { to: 'auditoria-ia', label: 'Auditoria de IA', permission: 'audit.read' },
];

export function SettingsLayout() {
  const { permissions } = useSession();
  const loc = useLocation();
  const tabs = SETTINGS_TABS.filter((t) => permissions.has(t.permission));
  if (tabs.length === 0) return <Forbidden />;
  if (loc.pathname.replace(/\/$/, '') === '/configuracoes') return <Navigate to={tabs[0]!.to} replace />;
  return (
    <>
      <PageHeader title="Configurações" description="Dados da loja, equipe e regras de funcionamento." />
      <nav aria-label="Seções de configurações" className="-mx-4 mb-6 overflow-x-auto overflow-y-hidden border-b px-4 sm:mx-0 sm:px-0">
        <ul className="flex min-w-max gap-1">
          {tabs.map((t) => (
            <li key={t.to}>
              <NavLink
                to={t.to}
                className={({ isActive }) =>
                  cn(
                    'relative flex h-11 items-center px-3.5 text-sm font-medium transition-colors',
                    isActive
                      ? 'text-foreground after:absolute after:inset-x-3 after:bottom-0 after:h-0.5 after:rounded-full after:bg-primary'
                      : 'text-muted-foreground hover:text-foreground',
                  )
                }
              >
                {t.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
      <Suspense
        fallback={
          <div role="status" aria-label="Carregando" className="flex flex-col gap-3">
            <Skeleton className="h-10 max-w-md" />
            <Skeleton className="h-10" />
          </div>
        }
      >
        <Outlet />
      </Suspense>
    </>
  );
}

/** Garante a permissão da aba mesmo se a URL for digitada à mão. */
export function SettingsGate({ permission, children }: { permission: Permission; children: React.ReactNode }) {
  const { permissions } = useSession();
  return permissions.has(permission) ? children : <Forbidden />;
}
